import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from '@mui/material';
import { useApiService } from '../../hooks/useApiService';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { standardPoolApi } from '../../services/standardPoolApi';
import {
  fetchPrimaryPoolSlots,
  PRIMARY_POOLS_UPDATED_EVENT,
} from '../../services/dashboardSettingsService';
import { sortPoolsByPrimaryOrder } from '../../utils/sortPoolsByPrimaryOrder';
import { filterAndSortPools, type PoolListSortKey, type PoolListSortOrder } from '../../utils/poolListSortAndFilter';

const PoolList: React.FC = () => {
  const navigate = useNavigate();
  const { poolApi } = useApiService();
  const [pools, setPools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    poolId: null as number | null,
    poolName: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<PoolListSortKey>('name');
  const [sortOrder, setSortOrder] = useState<PoolListSortOrder>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [paginationMeta, setPaginationMeta] = useState({ total_count: 0, total_pages: 1, page: 1, page_size: 20 });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  const fetchPools = useCallback(async () => {
    try {
      setLoading(true);
      console.log('시설 목록 로딩 시작...');
      const response = await poolApi.getPools({
        search: searchQuery || undefined,
        sort: sortKey,
        order: sortOrder,
        page,
        page_size: pageSize,
      });
      
      console.log('시설 목록 API 응답:', response);
      
      if (response.success && response.data) {
        const payload = response.data as any;
        const poolItems = Array.isArray(payload.pools) ? payload.pools : [];
        if (poolItems.length > 0) {
          console.log('시설 목록 로딩 성공:', poolItems.length + '개 항목');
          let slots: { code: string; order: number }[] = [];
          try {
            slots = await fetchPrimaryPoolSlots();
          } catch {
            slots = [];
          }
          setPools(sortPoolsByPrimaryOrder(poolItems, slots));
          setPaginationMeta({
            total_count: payload.total_count ?? 0,
            total_pages: payload.total_pages ?? 1,
            page: payload.page ?? 1,
            page_size: payload.page_size ?? pageSize,
          });
          setError(null);
        } else {
          console.log('시설 데이터가 없습니다.');
          setPools([]);
          setPaginationMeta({ total_count: 0, total_pages: 1, page: 1, page_size: pageSize });
          setError(null);
        }
      } else {
        console.error('시설 목록을 가져오는데 실패했습니다:', response.message);
        setError('시설 목록을 가져오기 실패: ' + response.message);
        setPools([]);
      }
    } catch (err) {
      console.error('시설 목록을 가져오는 중 오류 발생:', err);
      setError('시설 목록을 불러오기 실패');
      setPools([]);
    } finally {
      setLoading(false);
    }
  }, [poolApi, searchQuery, sortKey, sortOrder, page, pageSize]);

  useEffect(() => {
    fetchPools();
    const onUpdated = () => void fetchPools();
    window.addEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(PRIMARY_POOLS_UPDATED_EVENT, onUpdated);
  }, [fetchPools]);

  const handlePoolClick = (pool: any) => {
    console.log(`시설 선택: ${pool.name} (${pool.code})`);
    navigate(`/pool-management/${pool.code}`);
  };

  const handleDeleteClick = (event: React.MouseEvent, pool: any) => {
    event.stopPropagation();
    setDeleteDialog({
      open: true,
      poolId: pool.id,
      poolName: pool.name
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.poolId) {
      try {
        const response = await standardPoolApi.deletePool(deleteDialog.poolId);
        if (response.success) {
          setSnackbar({
            open: true,
            message: '시설이 삭제 완료',
            severity: 'success'
          });
          fetchPools(); // 목록 새로고침
        } else {
          setSnackbar({
            open: true,
            message: `삭제 실패: ${response.message || '알 수 없는 오류'}`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('시설 삭제 중 오류:', error);
        setSnackbar({
          open: true,
          message: '시설 삭제 중 오류 발생',
          severity: 'error'
        });
      }
    }
    setDeleteDialog({
      open: false,
      poolId: null,
      poolName: ''
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      poolId: null,
      poolName: ''
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const visiblePools = filterAndSortPools(pools, searchQuery, sortKey, sortOrder);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>오류</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (visiblePools.length === 0) {
    return (
      <Alert severity="info">
        <AlertTitle>안내</AlertTitle>
        등록된 시설 없음
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          시설 목록
        </Typography>
        <IconButton onClick={() => fetchPools()} title="새로고침">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="검색"
          placeholder="시설명/코드/생성일/주소"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="pool-sort-key-label">정렬 기준</InputLabel>
          <Select
            labelId="pool-sort-key-label"
            value={sortKey}
            label="정렬 기준"
            onChange={(e) => setSortKey(e.target.value as PoolListSortKey)}
          >
            <MenuItem value="name">시설명</MenuItem>
            <MenuItem value="code">시설코드</MenuItem>
            <MenuItem value="created_at">시설 생성 최신일</MenuItem>
            <MenuItem value="address">주소</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="pool-sort-order-label">정렬 방향</InputLabel>
          <Select
            labelId="pool-sort-order-label"
            value={sortOrder}
            label="정렬 방향"
            onChange={(e) => setSortOrder(e.target.value as PoolListSortOrder)}
          >
            <MenuItem value="asc">오름차순</MenuItem>
            <MenuItem value="desc">내림차순</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          총 {paginationMeta.total_count}개 · {paginationMeta.page}/{paginationMeta.total_pages} 페이지
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="pool-page-size-label">페이지 크기</InputLabel>
          <Select
            labelId="pool-page-size-label"
            value={pageSize}
            label="페이지 크기"
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10개</MenuItem>
            <MenuItem value={20}>20개</MenuItem>
            <MenuItem value={50}>50개</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Grid container spacing={2}>
        {visiblePools.map((pool) => (
          <Grid item xs={12} sm={6} md={4} key={pool.id}>
            <Card 
              variant="outlined"
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handlePoolClick(pool)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" gutterBottom>
                    {pool.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, pool)}
                    title="삭제"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  위치: {pool.location || pool.address || '정보 없음'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  코드: {pool.code || '정보 없음'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={pool.status === 'active' ? '운영 중' : '유지보수 중'} 
                    color={pool.status === 'active' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" color="primary">
                  상세 보기
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={paginationMeta.total_pages}
          page={Math.max(paginationMeta.page - 1, 0)}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* 삭제 확인 대화상자 */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>시설 삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteDialog.poolName}" 시설 삭제 확인
            이 작업은 되돌리기 불가
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>취소</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PoolList; 